import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

const SESSION_FLAG = 'division240_reminder_shown';

// Référence des équipements requis par catégorie de navigation (cf. Division240Selector).
// `nom` values are matched against equipment names persisted in the database and must stay in
// French. `key` is only used to look up the translated display label (division240Reminder.equipmentList).
interface Equipement { nom: string; key: string }

const EQUIPEMENTS_BASIQUE: Equipement[] = [
  { nom: 'Équipement individuel de flottabilité', key: '0' },
  { nom: 'Dispositif lumineux', key: '1' },
  { nom: "Moyens mobiles de lutte contre l'incendie", key: '2' },
  { nom: "Dispositif d'assèchement manuel", key: '3' },
  { nom: 'Dispositif de remorquage', key: '4' },
  { nom: 'Ligne de mouillage', key: '5' },
  { nom: 'Annuaire des marées', key: '6' },
  { nom: "Dispositif de repérage et d'assistance personne à la mer (bouée)", key: '7' },
];
const EQUIPEMENTS_COTIER: Equipement[] = [
  { nom: '3 feux rouges à main', key: '8' },
  { nom: 'Compas magnétique (ou GPS)', key: '9' },
  { nom: 'Cartes marines officielles', key: '10' },
];
const EQUIPEMENTS_SEMI_HAUTURIER: Equipement[] = [
  { nom: 'Pavillon national', key: '11' },
  { nom: 'Règlement RIPAM', key: '12' },
  { nom: 'Description du système de balisage', key: '13' },
  { nom: 'Livre des feux tenu à jour', key: '14' },
  { nom: 'Harnais et longe', key: '15' },
  { nom: 'VHF fixe', key: '16' },
];
const EQUIPEMENTS_HAUTURIER: Equipement[] = [
  { nom: 'Radeau de survie', key: '17' },
  { nom: 'Matériel pour faire le point', key: '18' },
  { nom: 'Journal de bord', key: '19' },
  { nom: 'Dispositif de réception météo', key: '20' },
  { nom: 'Trousse de secours', key: '21' },
  { nom: 'Dispositif lumineux recherche de nuit', key: '22' },
  { nom: 'Radiobalise EPIRB', key: '23' },
  { nom: 'VHF fixe + VHF portative', key: '24' },
];

const getEquipementsRequis = (categorie: string): Equipement[] => {
  if (categorie.includes('Hauturier') && !categorie.includes('Semi'))
    return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER, ...EQUIPEMENTS_HAUTURIER];
  if (categorie.includes('Semi-hauturier'))
    return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER];
  if (categorie.includes('Côtier')) return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER];
  if (categorie.includes('Basique')) return [...EQUIPEMENTS_BASIQUE];
  return [];
};

interface BateauManquants {
  bateauNom: string;
  bateauId: string;
  categorie: string;
  manquants: Equipement[];
}

const Division240Reminder = () => {
  const { user } = useAuth();
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bateauxAvecManquants, setBateauxAvecManquants] = useState<BateauManquants[]>([]);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(SESSION_FLAG) === '1') return;

    const checkEquipements = async () => {
      try {
        const { data: bateaux } = await supabase
          .from('bateaux')
          .select('id, nom')
          .eq('user_id', user.id);

        if (!bateaux || bateaux.length === 0) return;

        const { data: articles } = await supabase
          .from('inventaire')
          .select('nom, sous_categorie, bateau_id, quantite')
          .eq('user_id', user.id)
          .eq('categorie', 'Sécurité');

        const resultats: BateauManquants[] = [];

        for (const bateau of bateaux) {
          const articlesBateau = (articles || []).filter(a => a.bateau_id === bateau.id);
          // Détecter la catégorie via les sous-catégories Division 240 déjà présentes
          const categoriesPresentes = Array.from(
            new Set(
              articlesBateau
                .map(a => a.sous_categorie || '')
                .filter(sc => sc.startsWith('Division 240 -'))
            )
          );

          if (categoriesPresentes.length === 0) continue; // pas encore configuré → on ne harcèle pas

          // Prendre la catégorie la plus large déjà sélectionnée
          const ordre = ['Basique', 'Côtier', 'Semi-hauturier', 'Hauturier'];
          const categoriePlusLarge = categoriesPresentes
            .map(sc => sc.replace('Division 240 - ', ''))
            .sort((a, b) => ordre.indexOf(b) - ordre.indexOf(a))[0];

          const requis = getEquipementsRequis(categoriePlusLarge);
          const nomsPresents = articlesBateau
            .filter(a => a.quantite > 0)
            .map(a => a.nom.toLowerCase());

          const manquants = requis.filter(eq => !nomsPresents.includes(eq.nom.toLowerCase()));

          if (manquants.length > 0) {
            resultats.push({
              bateauNom: bateau.nom,
              bateauId: bateau.id,
              categorie: categoriePlusLarge,
              manquants,
            });
          }
        }

        if (resultats.length > 0) {
          setBateauxAvecManquants(resultats);
          setOpen(true);
          sessionStorage.setItem(SESSION_FLAG, '1');
        }
      } catch (err) {
        console.error('Division240Reminder: error', err);
      }
    };

    // léger délai pour laisser l'UI se charger
    const t = setTimeout(checkEquipements, 2500);
    return () => clearTimeout(t);
  }, [user]);

  const handleVoirInventaire = () => {
    setOpen(false);
    navigate('/inventory');
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="border-2 border-nautic-gold bg-gradient-to-br from-background to-nautic-gold/5">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-nautic-gold/20 border border-nautic-gold/40">
              <AlertTriangle className="h-6 w-6 text-nautic-gold" />
            </div>
            <Badge variant="outline" className="border-nautic-gold text-nautic-gold bg-nautic-gold/10">
              Division 240
            </Badge>
          </div>
          <AlertDialogTitle className="text-nautic">
            {t('division240Reminder.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                {t('division240Reminder.intro')}
              </p>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {bateauxAvecManquants.map((b, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-nautic-gold/30 bg-nautic-gold/5 p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-foreground">⛵ {b.bateauNom}</p>
                      <Badge variant="outline" className="text-xs">{b.categorie}</Badge>
                    </div>
                    <ul className="space-y-1">
                      {b.manquants.slice(0, 5).map((eq, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Shield className="h-3 w-3 text-nautic-gold mt-0.5 shrink-0" />
                          <span>{t(`division240Reminder.equipmentList.${eq.key}`)}</span>
                        </li>
                      ))}
                      {b.manquants.length > 5 && (
                        <li className="text-xs italic text-nautic-gold">
                          {t('division240Reminder.moreEquipment', { count: b.manquants.length - 5 })}
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('division240Reminder.later')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleVoirInventaire}
            className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
          >
            {t('division240Reminder.completeInventory')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default Division240Reminder;
